/** @jsxImportSource @emotion/react */
import { SerializedStyles, css } from "@emotion/react";
import { FunctionComponent, useRef, useState } from "react";

import { StrapiTerminusTopic } from "@/types/strapi";

import { Colors } from "@/tokens/color";
import { Spacing } from "@/tokens/spacing";

import { Icon } from "@/ui/atoms/icon";
import { Link } from "@/ui/atoms/link";
import { Text } from "@/ui/atoms/text";

import { useSidebarLinksAnimation } from "@/util/animation_hooks/terminus_sidebar_animations";
import { useArrayRef } from "@/util/hooks/ref_hooks";
import { useTypedTheme } from "@/util/hooks/theme_hooks";

interface TerminusSidebarTopicsProps {
    className?: SerializedStyles;
    isCollapsible?: boolean;
    numTopics?: number;
    topics?: StrapiTerminusTopic[];
}

export const TerminusSidebarTopics: FunctionComponent<
    TerminusSidebarTopicsProps
> = (props) => {
    const { isCollapsible, numTopics = 5, topics } = props;

    /**
     * Refs
     */
    const buttonRef = useRef<HTMLButtonElement>(null);
    const iconRef = useRef(null);
    const listContentContainerRef = useRef<HTMLUListElement>(null);
    const [listItemsRef, setListItemsRef] = useArrayRef();

    /**
     * State
     */
    const [isExpanded, setIsExpanded] = useState(true);

    /**
     * Hooks
     */

    useSidebarLinksAnimation({
        buttonRef,
        containerRef: listContentContainerRef,
        iconRef,
        isExpanded,
        itemsRef: listItemsRef,
    });

    /**
     * Theme
     */
    const theme = useTypedTheme();

    if (!topics) {
        return null;
    }

    /**
     * Interactivity
     */
    const handleOnClick = () => {
        setIsExpanded(!isExpanded);
    };

    /**
     * Styles
     */
    const topicsContainerStyles = css({
        padding: Spacing["spacing-2"],
    });

    const topicListStyles = css({
        display: "flex",
        flexFlow: "column",
        gridRowGap: Spacing["spacing-1"],
        overflow: "hidden",
    });

    const topicsButtonStyles = css({
        alignItems: "center",
        backgroundColor: Colors["transparent"],
        border: "none",
        cursor: "pointer",
        display: "flex",
        gap: Spacing["spacing-2"],
        marginBottom: Spacing["spacing-4"],
        padding: Spacing["spacing-0"],
        position: "relative",
    });

    const linkFlagStyles = css({
        height: Spacing["spacing-3"],
        marginBottom: Spacing["spacing-1"],
        rotate: "90deg",
        width: Spacing["spacing-3"],
    });

    return (
        <div css={topicsContainerStyles}>
            {isCollapsible ? (
                <button
                    css={topicsButtonStyles}
                    ref={buttonRef}
                    onClick={handleOnClick}
                >
                    <Text isUppercase fontFamily="mono" fontSize="TEXT_075">
                        Topics
                    </Text>

                    <Icon
                        className={linkFlagStyles}
                        color={theme.text.textEmphasis}
                        fontSize="TEXT_0625"
                        ref={iconRef}
                        slug="Caret Right"
                    />
                </button>
            ) : (
                <Text
                    isUppercase
                    fontFamily="mono"
                    fontSize="TEXT_075"
                    marginBottom="TerminusSidebarTopicsMarginBottom"
                    themeKey="textSecondary"
                >
                    Topics
                </Text>
            )}

            <ul css={topicListStyles} ref={listContentContainerRef}>
                {topics.slice(0, numTopics).map((topic) => {
                    return (
                        <li
                            key={`terminus-sidebar::topics::${topic.Slug}`}
                            ref={setListItemsRef}
                        >
                            <Link
                                fontSize="TextSmall"
                                fontWeight="normal"
                                href={`/topic/${topic.Slug}`}
                                interactionType="hover-color-change"
                                labelLineHeight={false}
                                themeKey="linkPrimary"
                            >
                                {topic.Name}
                            </Link>
                        </li>
                    );
                })}
            </ul>

            <Link
                renderAsBlock
                fontSize="TextSmall"
                href="/terminus-topics"
                interactionType="hover-color-change"
                marginTop="Default"
                themeKey="linkPrimary"
            >
                View all topics
            </Link>
        </div>
    );
};
